import {
  Button,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AddIcon from '@mui/icons-material/Add';
import { useState } from "react";
import SearchBar from "../../../components/SearchBar";
import CommonBreadcrumb from "../../../components/CommonBreadcrumb";
import SwitchToggle from "../../../components/SwitchToggle";
import AddTransmission from "../../../Modals/addTransmission";
import React from "react";
import { useGetCategoriesQuery, useGetTransmissionQuery, useDeleteTransmissionMutation, useUpdateTransmissionMutation } from "../../../service/features/api";
import Pagination from "../../../components/Panigation";
import { usePermission } from "../../../hooks/usePermission";
import Can from "../../../components/Can";
import moment from "moment";
import DeleteConfirmModal from "../../../Modals/DeleteConfirmModal";
import { toast } from "react-toastify";
import { VEHICLE_TYPE } from "../../../constants/enum";
import { getVehicleTypeLabel } from "../../../utils/vehicle";

const ManageTransmission = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canEdit = hasPermission("Vehicle Specification", "isEdit");
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState('');
  const [vehicleType, setVehicleType] = useState<string>('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [openModal, setOpenModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedTransmission, setSelectedTransmission] = useState<any>(null);

  // Queries
  const { data: categoriesData } = useGetCategoriesQuery({ page: 1, limit: 100, type: vehicleType || undefined });

  const { data: transmissionData, isLoading, isFetching } = useGetTransmissionQuery({
    page,
    limit,
    search: searchTerm,
    categoryId: category || undefined,
    type: vehicleType || undefined,
  });

  const [deleteTransmission, { isLoading: isDeleting }] = useDeleteTransmissionMutation();
  const [updateTransmission] = useUpdateTransmissionMutation();

  const handleCategoryChange = (event: SelectChangeEvent) => {
    setCategory(event.target.value as string);
    setPage(1);
  };

  const handleVehicleTypeChange = (event: SelectChangeEvent) => {
    setVehicleType(event.target.value as string);
    setCategory('');
    setPage(1);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedTransmission(null);
  };

  const handleEdit = (item: any) => {
    setSelectedTransmission(item);
    setOpenModal(true);
  };

  const handleDeleteClick = (item: any) => {
    setSelectedTransmission(item);
    setOpenDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedTransmission?._id) return;
    try {
      await deleteTransmission(selectedTransmission._id).unwrap();
      toast.success("Transmission deleted successfully / تم حذف ناقل الحركة بنجاح");
      setOpenDeleteModal(false);
      setSelectedTransmission(null);
      if (transmissionList.length === 1 && page > 1) {
        setPage(page - 1);
      }
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to delete Transmission / فشل حذف ناقل الحركة");
    }
  };

  const handleToggle = async (item: any, field: string, value: boolean) => {
    const payload = {
      id: item._id,
      name: item.name,
      categoryId: item.categoryId || (item.category?.map((c: any) => c._id) || []),
      isActive: item.isActive,
      type: item.type || item.vehicleType,
      [field]: value
    };

    try {
      await updateTransmission(payload).unwrap();
      toast.success("Updated successfully / تم التحديث بنجاح");
    } catch (err: any) {
      toast.error(err?.data?.message || "Update failed / فشل التحديث");
    }
  };

  const transmissionList = transmissionData?.data?.data || [];
  const paginator = transmissionData?.data?.paginator || {};

  return (
    <>
      <CommonBreadcrumb
        title="Transmission Management / إدارة ناقل الحركة"
        breadcrumbItems={[
          { label: "Dashboard / لوحة المعلومات", onClick: () => navigate("/dashboard") },
          { label: "Listed Vehicles / المركبات المدرجة", onClick: () => navigate("/manage-vehicles") },
          { label: "Transmission Management / إدارة ناقل الحركة" },
        ]}
      />

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              searchTerm={searchTerm}
              setDebouncedSearchTerm={(term) => {
                setSearchTerm(term);
                setPage(1);
              }}
              placeholder="Search Transmissions... / ابحث عن ناقل الحركة..."
            />
            <div className="form">
              <div className="control_group">
                <Select
                  fullWidth
                  value={vehicleType}
                  onChange={handleVehicleTypeChange}
                  displayEmpty
                >
                  <MenuItem value="">Select Vehicle Type / اختر نوع المركبة</MenuItem>
                  {Object.values(VEHICLE_TYPE)
                    .filter((v) => typeof v === "number")
                    .map((type) => (
                      <MenuItem key={type} value={type.toString()}>
                        {getVehicleTypeLabel(type as number)}
                      </MenuItem>
                    ))}
                </Select>
              </div>
              <div className="control_group">
                <Select
                  fullWidth
                  value={category}
                  onChange={handleCategoryChange}
                  displayEmpty
                >
                  <MenuItem value="">Select Category / اختر الفئة</MenuItem>
                  {categoriesData?.data?.data?.map((cat: any) => (
                    <MenuItem key={cat._id} value={cat._id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            </div>
          </div>
          <div className="right_s">
            <Can module="Vehicle Specification" action="isAdd" isDisabledMode={true}>
              <Button
                className="btn btn_primary"
                onClick={() => {
                  setSelectedTransmission(null);
                  setOpenModal(true);
                }}
              >
                <AddIcon /> Add Transmission / إضافة ناقل حركة
              </Button>
            </Can>
          </div>
        </div>

        <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Sr. No. / الرقم التسلسلي</TableCell>
                <TableCell>Transmission Detail / تفاصيل ناقل الحركة</TableCell>
                <TableCell>Categories / الفئات</TableCell>
                <TableCell>Created Date / تاريخ الإنشاء</TableCell>
                <TableCell>Disable/Enable / تعطيل/تفعيل</TableCell>
                <TableCell>Actions / الإجراءات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading || isFetching ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" style={{ padding: '2rem' }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : transmissionList.length > 0 ? (
                transmissionList.map((row: any, index: number) => (
                  <TableRow hover key={row._id}>
                    <TableCell>{(paginator.currentPage - 1) * paginator.perPage + index + 1}</TableCell>
                    <TableCell>
                      <div className="user_block">
                        <p>
                          <b>{row.name || "--"}</b>
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      {(row.category || row.categories || row.categoryId)?.map((c: any) => c.name).join(", ") || (Array.isArray(row.categoryName) ? row.categoryName.join(", ") : row.categoryName) || "--"}
                    </TableCell>
                    <TableCell>{moment(row.createdAt).format('DD-MMM-YYYY')}</TableCell>
                    <TableCell>
                      {canEdit ? (
                        <SwitchToggle
                          checked={row.isActive}
                          onChange={(e) => handleToggle(row, "isActive", e.target.checked)}
                        />
                      ) : (
                        <span className={`status_btn ${row.isActive ? "c_dark_info" : "c_dark_warning"}`}>
                          {row.isActive ? "Enabled / مفعل" : "Disabled / معطل"}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="table_actions v2">
                        <Can module="Vehicle Specification" action="isEdit" isDisabledMode={true}>
                          <Tooltip title="Edit / تعديل" arrow>
                            <IconButton
                              onClick={() => handleEdit(row)}
                            >
                              <img src="/static/images/edit2_icon.svg" alt="Edit / تعديل" />
                            </IconButton>
                          </Tooltip>
                        </Can>
                        <Can module="Vehicle Specification" action="isDelete" isDisabledMode={true}>
                          <Tooltip title="Delete / حذف" arrow>
                            <IconButton
                              onClick={() => handleDeleteClick(row)}
                            >
                              <img src="/static/images/trash2_icon.svg" alt="Delete / حذف" />
                            </IconButton>
                          </Tooltip>
                        </Can>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No Transmissions found / لا توجد أنواع ناقل حركة
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {paginator && paginator.pageCount > 1 && (
          <Pagination
            module={transmissionList}
            page={page}
            onPageChange={(p) => setPage(p)}
            totalPages={paginator.pageCount}
            totalCount={paginator.itemCount}
            setPage={setPage}
          />
        )}
      </div>

      <AddTransmission
        open={openModal}
        onClose={handleCloseModal}
        setOpen={setOpenModal}
        transmissionData={selectedTransmission}
      />

      <DeleteConfirmModal
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        message={`Are you sure you want to delete the transmission "${selectedTransmission?.name}"? / هل أنت متأكد أنك تريد حذف ناقل الحركة "${selectedTransmission?.name}"؟`}
      />
    </>
  );
};

export default ManageTransmission;
